/**
 * The note a quote is about, shown inside the composer.
 *
 * Deliberately *not* a `QuoteCard`. That component resolves a reference through the
 * app-wide interest set and opens a subscription for it, which is exactly right in a
 * feed and pointless here: the composer was handed the event. Rendering the card
 * would ask the network for a note already in hand, and drag the quote-depth frame
 * into a surface that has no depth.
 *
 * So this is a flat preview with three properties the real card has and a naive one
 * would not:
 *
 *  - **The text is plain and clamped.** No media, no nested quote, no link
 *    tokenizing. What is being confirmed is *which* note this is, and a preview that
 *    renders attachments competes with the composer for the reader's attention.
 *  - **The author is resolved, not assumed.** A quote of someone whose kind-0 has
 *    not arrived shows a truncated npub rather than a blank line, because a nameless
 *    box gives the reader no way to check they picked the right note.
 *  - **It never claims the note is verified or current.** It is a snapshot of what
 *    this device holds, which is all the composer needs it to be.
 */

import { encodeNpub, type NostrEvent, truncateNpub } from "@setu/protocol";
import { Avatar, AvatarFallback, AvatarImage, cn } from "@setu/ui";
import { useAuthors } from "../profiles/useAuthors";

/**
 * How much of the quoted note to show.
 *
 * Two lines' worth. Long enough to identify the note, short enough that the
 * composer's own textarea stays the thing being looked at.
 */
const MAX_PREVIEW = 220;

export function QuotedPreview({
  target,
  className,
}: {
  target: NostrEvent;
  className?: string;
}) {
  const authors = useAuthors([target.pubkey]);
  const author = authors.get(target.pubkey);
  const name =
    author?.displayName ??
    // The npub, not the raw hex: a reader can compare an npub against one they
    // have seen elsewhere, and 64 hex characters tells them nothing.
    truncateNpub(encodeNpub(target.pubkey) ?? target.pubkey);

  const text = target.content.trim();
  const clamped =
    text.length > MAX_PREVIEW ? `${text.slice(0, MAX_PREVIEW)}…` : text;

  return (
    <div
      className={cn(
        "mt-2 rounded-md border border-border/60 bg-muted/30 px-2.5 py-2",
        className,
      )}
    >
      <div className="flex items-center gap-1.5">
        <Avatar className="size-4 shrink-0">
          {author?.avatarUrl ? (
            <AvatarImage src={author.avatarUrl} alt="" />
          ) : null}
          <AvatarFallback>{name.slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="truncate text-xs font-medium">{name}</span>
      </div>
      {clamped === "" ? (
        // A note with no text is normally a picture or video kind. Saying so beats
        // an empty box that reads as a failure to load.
        <p className="mt-1 text-xs text-muted-foreground italic">
          This note has no text of its own.
        </p>
      ) : (
        <p className="mt-1 text-xs leading-snug whitespace-pre-wrap text-muted-foreground">
          {clamped}
        </p>
      )}
    </div>
  );
}
